import React, {useEffect, useState} from 'react';

import {useOnClickOff} from '@newsela/angelou';
import {useFeature} from '@optimizely/react-sdk';
import {isEmpty, forEach, map} from 'lodash-es';
import PropTypes from 'prop-types';

import {AgathaServiceApi} from 'static/three-oh/src/modules/apis';
import {
  AGATHA_PAGE_SEARCH_FORM_ID,
  GLOBAL_NAV_SEARCH_FORM_ID,
  CLASSNAME_FOR_AGATHA_GENERATED_SUGGESTION,
} from 'static/three-oh/src/routes/Search/constants';
import {eventStream} from 'static/three-oh/utils/sessionActivity';

import AgathaSuggestion from './components/AgathaSuggestion/AgathaSuggestion';
import styles from './styles';

const componentDisplayName = 'AgathaSuggestionsContainer';
const propTypes = {
  isVisible: PropTypes.bool.isRequired,
  fragmentForSuggestionsFetch: PropTypes.string.isRequired,
  isGlobalNavSearch: PropTypes.bool,
  updateSuggestionsAreVisible: PropTypes.func,
  onSuggestionSelection: PropTypes.func.isRequired,
  updateSuggestions: PropTypes.func.isRequired,
  suggestions: PropTypes.array.isRequired,
  trendingSuggestions: PropTypes.array.isRequired,
  updateTrendingSuggestions: PropTypes.func,
  inputNeedle: PropTypes.string,
};

const defaultProps = {
  isGlobalNavSearch: false,
};

const DATA_INDEX_NUMBER_OF_FIRST_SUGGESTION = 0;
const NUMBER_OF_SUGGESTIONS_TO_FETCH_PER_REQUEST = 5;

const AgathaSuggestionsContainer = (props) => {
  const {
    inputNeedle,
    onSuggestionSelection,
    isVisible,
    fragmentForSuggestionsFetch,
    isGlobalNavSearch,
    updateSuggestionsAreVisible,
    updateSuggestions,
    suggestions,
    trendingSuggestions,
    updateTrendingSuggestions,
  } = props;
  const searchPageInputField = document.getElementById(AGATHA_PAGE_SEARCH_FORM_ID);
  const globalNavInputField = document.getElementById(GLOBAL_NAV_SEARCH_FORM_ID);
  const inputFieldElement = isGlobalNavSearch ? globalNavInputField : searchPageInputField;

  const [priorSearches, updatePriorSearches] = useState({});

  // Send data about the response to Kinesis' 'event-stream' table and the
  // legacy 'user-events' table.
  const sendDataAboutSuggestionsResponseToKinesis = (response) => {
    const legacyEventProperties = {
      objectType: 'Search Suggestion',
      actionName: 'Read',
      captured_url: window.location.pathname,
      trackImpression: true,
      eventProperties: {
        suggestions_id: response.suggestions_id,
        search_term: response.parameters.fragment,
      },
    };
    const eventStreamProperties = {
      description: `Search Suggestions generated by the fragment ${response.parameters.fragment}`,
      current_url: window.location.pathname,
      application: 'agatha',
      search_id: response.suggestions_id,
      request_type: 'suggestions',
      count_results_retrieved: response.results.length,
      query_term: response.parameters.fragment,
      results_identifier: map(response.results, 'suggestion'),
      schema_version: '1.0',
      search_parameters: response.parameters,
      used_strategy: response.parameters.strategy,
      component_type: componentDisplayName,
    };
    eventStream(eventStreamProperties, legacyEventProperties);
  };

  const [useAgathaOnECS] = useFeature(
    'use_agatha_on_ecs',
    {autoUpdate: true},
  );

  const shapeSuggestions = (response) => {
    return map(response.data.results, (result) => {
      return {
        text: result.suggestion,
        suggestionId: response.data.suggestions_id,
      };
    });
  };

  const [userShouldSeeRankedSuggestions] = useFeature(
    'ranked_auto_suggestions_experiment',
    {autoUpdate: true},
  );

  // Initiate a request to retrieve suggestions that map with the needle.
  const fetchSuggestions = async() => {
    try {
      const params = {
        fragment: fragmentForSuggestionsFetch,
        page_size: NUMBER_OF_SUGGESTIONS_TO_FETCH_PER_REQUEST,
      };

      if (userShouldSeeRankedSuggestions) {
        params['strategy'] = 'ranked';
      }

      const response = await AgathaServiceApi.getSuggestions(useAgathaOnECS, params);
      const suggestions = shapeSuggestions(response);

      // Store/cache suggestions in local state for re-use, so the client
      // doesn't to duplicate this fetch during the life-cycle of this component.
      priorSearches[fragmentForSuggestionsFetch] = suggestions;
      updatePriorSearches(priorSearches);

      sendDataAboutSuggestionsResponseToKinesis(response.data);
      updateSuggestions(suggestions);
    } catch (error) {
      updateSuggestions([]);
    }
  };

  const fetchTrendingSuggestions = async() => {
    try {
      const params = {
        fragment: '',
        page_size: NUMBER_OF_SUGGESTIONS_TO_FETCH_PER_REQUEST,
      };
      const response = await AgathaServiceApi.getSuggestions(useAgathaOnECS, params);
      const suggestions = shapeSuggestions(response);
      sendDataAboutSuggestionsResponseToKinesis(response.data);
      updateTrendingSuggestions(suggestions);
    } catch (error) {
      updateTrendingSuggestions([]);
    }
  };

  useEffect(() => {
    // Only fetch suggestions for *new* fragments.
    if (fragmentForSuggestionsFetch && !priorSearches.hasOwnProperty(fragmentForSuggestionsFetch)) {
      fetchSuggestions();
    } else {
      updateSuggestions([]);
    }
  }, [fragmentForSuggestionsFetch]);

  useEffect(() => {
    fetchTrendingSuggestions();
  }, []); // Fetch trending suggestions once. We store the results in local
  // state and will display those queries for the duration of this component's
  // life-cycle, which will improve UX, as users will see trending queries
  // quicker.

  const applyFocusToNextSuggestion = (elementIndexNumber) => {
    const searchSuggestions = document.querySelectorAll(`.${CLASSNAME_FOR_AGATHA_GENERATED_SUGGESTION}`);
    const isLastSuggestion = elementIndexNumber === searchSuggestions.length - 1;

    // If it's not the last item on the list, apply focus to the next suggestion.
    if (!isLastSuggestion) {
      applyFocusToSuggestion(elementIndexNumber + 1);
    }
  };

  const applyFocusToPreviousSuggestion = (elementIndexNumber) => {
    if (elementIndexNumber == DATA_INDEX_NUMBER_OF_FIRST_SUGGESTION && inputFieldElement) {
      // Apply focus to the input field if the 'arrow-up' key is selected on the
      // first suggestion.
      updateSuggestionsAreVisible(true);
      inputFieldElement.focus();
    } else {
      applyFocusToSuggestion(elementIndexNumber - 1);
    }
  };

  const applyFocusToSuggestion = (elementIndexNumber) => {
    const suggestions = document.querySelectorAll(`.${CLASSNAME_FOR_AGATHA_GENERATED_SUGGESTION}`);
    if (isEmpty(suggestions)) {
      return;
    }

    forEach(suggestions, (suggestion) => {
      if (suggestion.getAttribute('data-index-number') == elementIndexNumber) {
        suggestion.focus();
      }
    });
  };

  const getSuggestionsToDisplay = () => {
    if (inputNeedle) {
      // If the client has already fetched suggestions for this needle, use the
      // cached suggestions that are stored in local state.
      return (
        priorSearches.hasOwnProperty(inputNeedle) ?
          priorSearches[inputNeedle] :
          suggestions
      );
    } else {
      return trendingSuggestions;
    }
  };

  const renderSuggestions = () => {
    if (isEmpty(getSuggestionsToDisplay())) {
      return null;
    }

    return map(getSuggestionsToDisplay(), (suggestion, index) => {
      return (
        <AgathaSuggestion
          applyFocusToNextSuggestion={applyFocusToNextSuggestion}
          applyFocusToPreviousSuggestion={applyFocusToPreviousSuggestion}
          fragmentForSuggestionsFetch={fragmentForSuggestionsFetch}
          key={`${suggestion.text}-${index}`}
          onSuggestionSelection={onSuggestionSelection}
          suggestion={suggestion}
          dataIndexNumber={index}
          isGlobalNavSearch={isGlobalNavSearch}
          updateSuggestionsAreVisible={updateSuggestionsAreVisible}
          userShouldSeeRankedSuggestions={userShouldSeeRankedSuggestions}
        />
      );
    });
  };

  const clickAwayRef = useOnClickOff((event) => {
    const isSearchPageForm = event.target.id === AGATHA_PAGE_SEARCH_FORM_ID;
    const isGlobalNavForm = event.target.id === GLOBAL_NAV_SEARCH_FORM_ID;

    // If the user clicks outside of the suggestions listing and the subsequent
    // element is neither of the search forms, close the drop-down menu.
    if (!isSearchPageForm && !isGlobalNavForm) {
      updateSuggestionsAreVisible(false);
    }
  });

  const suggestionsForTheGlobalNavBar = (
    <div
      aria-label="Agatha Search Suggestions"
      className={styles.globalNavAgathaSuggestionsContainer}
      data-qa-selector="agatha_search_suggestion_list"
    >
      <ul
        className={styles.globalNavList}
      >
        {renderSuggestions()}
      </ul>
    </div>
  );

  const suggestionsListing = (
    isGlobalNavSearch ?
      suggestionsForTheGlobalNavBar :
        (
          <div
            ref={clickAwayRef}
            className={styles.suggestionWrapper}
            data-qa-selector="agatha_search_suggestion_list"
          >
            <ul
              aria-label="Agatha Search Suggestions"
              className={styles.suggestionList}
              data-qa-selector="agatha_search_suggestion_list"
              role="listbox"
            >
              {renderSuggestions()}
            </ul>
          </div>
        )
  );

  return (
    isVisible && !isEmpty(getSuggestionsToDisplay()) ?
      suggestionsListing :
      null
  );
};

AgathaSuggestionsContainer.propTypes = propTypes;
AgathaSuggestionsContainer.displayName = componentDisplayName;
AgathaSuggestionsContainer.defaultProps = defaultProps;

export default AgathaSuggestionsContainer;
